import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'
import { ResponsiveContainer, BarChart, Bar, Tooltip } from 'recharts'

var SVC_COLORS = { stt: '#22D3EE', tts: '#FB7185', llm: '#34D399' }

var SERVICE_CONFIG = {
  stt: {
    emoji: '🎤',
    name: 'Speech Recognition',
    description: 'Transcribes spoken audio to text',
    metricLabel: 'minutes transcribed',
    secondaryLabel: 'cost per minute',
    getMetric: function(cost, sessions) {
      var used = sessions.filter(function(s) { return s.serviceCosts && s.serviceCosts.stt > 0 })
      var totalMin = used.reduce(function(s, x) { return s + (x.durationMin || 0) }, 0)
      var costPerMin = totalMin > 0 ? cost / totalMin : 0
      return { primary: fmtDur(totalMin), secondary: fmtCost(costPerMin) + '/min' }
    },
  },
  tts: {
    emoji: '🔊',
    name: 'Voice Synthesis',
    description: 'Converts text to spoken audio',
    metricLabel: 'conversations used',
    secondaryLabel: 'avg cost / conversation',
    getMetric: function(cost, sessions) {
      var used = sessions.filter(function(s) { return s.serviceCosts && s.serviceCosts.tts > 0 }).length
      var avg = used > 0 ? cost / used : 0
      return { primary: used + ' conversations', secondary: fmtCost(avg) + '/conv' }
    },
  },
  llm: {
    emoji: '🤖',
    name: 'AI Language Model',
    description: 'Large language model inference',
    metricLabel: 'conversations used',
    secondaryLabel: 'avg cost / conversation',
    getMetric: function(cost, sessions) {
      var used = sessions.filter(function(s) { return s.serviceCosts && s.serviceCosts.llm > 0 }).length
      var avg = used > 0 ? cost / used : 0
      return { primary: used + ' conversations', secondary: fmtCost(avg) + '/conv' }
    },
  },
}

var useStyles = makeStyles(function(theme) {
  return {
    card: {
      padding: 24,
      border: '1px solid #e8ecf4',
      borderRadius: 12,
      position: 'relative',
      overflow: 'hidden',
    },
    topAccent: {
      position: 'absolute',
      top: 0, left: 0, right: 0,
      height: 3,
    },
    colorWash: {
      position: 'absolute',
      top: 0, left: 0,
      width: 180, height: 180,
      pointerEvents: 'none',
    },
    icon: {
      width: 48, height: 48, borderRadius: 14,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 22, marginBottom: 16, position: 'relative',
    },
    name: { fontSize: 14, fontWeight: 700, color: '#1a2540', marginBottom: 4 },
    desc: { fontSize: 12, color: '#777', marginBottom: 16 },
    bigCost: { fontSize: 32, fontWeight: 800, fontFamily: 'monospace', marginBottom: 16 },
    metricBox: {
      flex: 1, background: '#f8faff',
      borderRadius: 10, padding: '10px 12px',
      border: '1px solid #e8ecf4',
    },
    metricValue: { fontSize: 14, fontWeight: 700, color: '#1a2540' },
    metricLabel: { fontSize: 10, color: '#777', marginTop: 2 },
    latencyBox: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: '#f8faff', borderRadius: 10, padding: '10px 12px',
      marginBottom: 12, border: '1px solid #e8ecf4',
    },
    sessionCount: { fontSize: 11, color: '#777', marginBottom: 12 },
    emptyChart: {
      height: 48,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#ccc', fontSize: 11,
    },
    skeletonCard: {
      padding: 24, border: '1px solid #e8ecf4', borderRadius: 12,
    },
    skeleton: {
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
      backgroundSize: '200% 100%',
      animation: '$shimmer 1.5s infinite',
      borderRadius: 8,
    },
    '@keyframes shimmer': {
      '0%': { backgroundPosition: '200% 0' },
      '100%': { backgroundPosition: '-200% 0' },
    },
  }
})

function fmtCost(v) {
  if (v == null || v === 0) return '$0.0000'
  if (v < 0.01) return '$' + v.toFixed(5)
  return '$' + v.toFixed(4)
}

function fmtDur(min) {
  if (!min) return '0s'
  if (min < 1) return Math.round(min * 60) + 's'
  if (min < 60) return min.toFixed(1) + ' min'
  return (min / 60).toFixed(1) + 'h'
}

function fmtMs(ms) {
  if (!ms) return '—'
  if (ms >= 1000) return (ms / 1000).toFixed(2) + 's'
  return Math.round(ms) + 'ms'
}

function MiniTooltip(props) {
  if (!props.active || !props.payload || !props.payload.length) return null
  return (
    <div style={{ background: '#fff', border: '1px solid #e8ecf4', borderRadius: 7, padding: '5px 9px', fontSize: 11, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <span style={{ color: '#888' }}>{props.label}: </span>
      <span style={{ fontWeight: 700, color: '#1a2540' }}>{fmtCost(props.payload[0].value)}</span>
    </div>
  )
}

function SkeletonCard() {
  var classes = useStyles()
  return (
    <Paper className={classes.skeletonCard} elevation={0}>
      <div className={classes.skeleton} style={{ width: 48, height: 48, borderRadius: 14, marginBottom: 16 }} />
      <div className={classes.skeleton} style={{ width: '60%', height: 20, marginBottom: 8 }} />
      <div className={classes.skeleton} style={{ width: '40%', height: 14, marginBottom: 16 }} />
      <div className={classes.skeleton} style={{ width: '50%', height: 32, marginBottom: 16 }} />
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div className={classes.skeleton} style={{ flex: 1, height: 60, borderRadius: 10 }} />
        <div className={classes.skeleton} style={{ flex: 1, height: 60, borderRadius: 10 }} />
      </div>
      <div className={classes.skeleton} style={{ height: 48 }} />
    </Paper>
  )
}

function ServiceCard(props) {
  var classes = useStyles()
  var color = SVC_COLORS[props.svcKey] || '#94A3B8'
  var config = props.config
  var sessions = props.sessions || []
  var totalCost = props.totalCost || 0
  var latency = props.latency
  var dailySeries = props.dailySeries || []

  var metrics = sessions.length > 0 ? config.getMetric(totalCost, sessions) : { primary: '—', secondary: '—' }
  var sessionCount = sessions.filter(function(s) { return s.serviceCosts && s.serviceCosts[props.svcKey] > 0 }).length
  var chartData = dailySeries
    .map(function(d) { return { date: d.date, value: d[props.svcKey] || 0 } })
    .filter(function(d) { return d.value > 0 })

  return (
    <Paper className={classes.card} elevation={0}>
      {/* Top accent */}
      <div className={classes.topAccent} style={{ background: 'linear-gradient(90deg, ' + color + ', transparent)' }} />

      {/* Color wash */}
      <div className={classes.colorWash} style={{ background: 'radial-gradient(ellipse at top left, ' + color + '12, transparent 70%)' }} />

      {/* Icon */}
      <div className={classes.icon} style={{ background: color + '18', border: '1px solid ' + color + '30' }}>
        {config.emoji}
      </div>

      <Typography className={classes.name}>{config.name}</Typography>
      <Typography className={classes.desc}>{config.description}</Typography>

      {/* Big cost */}
      <Typography className={classes.bigCost} style={{ color: color }}>{fmtCost(totalCost)}</Typography>

      {/* Metric boxes */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div className={classes.metricBox}>
          <Typography className={classes.metricValue}>{metrics.primary}</Typography>
          <Typography className={classes.metricLabel}>{config.metricLabel}</Typography>
        </div>
        <div className={classes.metricBox}>
          <Typography className={classes.metricValue}>{metrics.secondary}</Typography>
          <Typography className={classes.metricLabel}>{config.secondaryLabel}</Typography>
        </div>
      </div>

      {/* Latency */}
      {latency && latency.avgMs != null && (
        <div className={classes.latencyBox}>
          <div>
            <Typography style={{ fontSize: 14, fontWeight: 700, color: '#1a2540' }}>⚡ {fmtMs(latency.avgMs)}</Typography>
            <Typography style={{ fontSize: 10, color: '#777', marginTop: 2 }}>avg response speed</Typography>
          </div>
          <Typography style={{ fontSize: 10, color: '#aaa', textAlign: 'right' }}>
            across {latency.samples} calls
          </Typography>
        </div>
      )}

      {/* Session count */}
      <Typography className={classes.sessionCount}>
        Used in <strong style={{ color: '#555' }}>{sessionCount}</strong> conversation{sessionCount !== 1 ? 's' : ''}
      </Typography>

      {/* Mini bar chart */}
      {chartData.length > 1 ? (
        <ResponsiveContainer width="100%" height={48}>
          <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} />
            <Tooltip content={<MiniTooltip />} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Typography className={classes.emptyChart}>No daily data in this range</Typography>
      )}
    </Paper>
  )
}

export default function ServicesTab(props) {
  var data = props.data || {}
  var loading = props.loading
  var svcTotals = data.svcTotals || {}
  var svcLatencies = data.svcLatencies || {}
  var sessions = data.sessions || []
  var dailySeries = data.dailySeries || []

  if (loading) {
    return (
      <Grid container spacing={2}>
        {[0, 1, 2, 3].map(function(i) {
          return (
            <Grid item xs={12} sm={6} key={i}>
              <SkeletonCard />
            </Grid>
          )
        })}
      </Grid>
    )
  }

  return (
    <Grid container spacing={2}>
      {Object.entries(SERVICE_CONFIG).map(function(entry) {
        var key = entry[0], config = entry[1]
        return (
          <Grid item xs={12} sm={6} key={key}>
            <ServiceCard
              svcKey={key}
              config={config}
              totalCost={svcTotals[key] || 0}
              sessions={sessions}
              dailySeries={dailySeries}
              latency={svcLatencies[key]}
              loading={loading}
            />
          </Grid>
        )
      })}
    </Grid>
  )
}
